import { HttpError, api } from '@/api/client'
import { Alert } from '@/components/ui/alert'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { useAuthStore } from '@/stores/auth.store'
import { cn } from '@/lib/cn'
import { zodResolver } from '@hookform/resolvers/zod'
import { useMutation } from '@tanstack/react-query'
import { createRoute, redirect, useNavigate } from '@tanstack/react-router'
import { motion } from 'framer-motion'
import { Eye, EyeOff, ShieldCheck } from 'lucide-react'
import { useState } from 'react'
import { useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { z } from 'zod'
import { Route as rootRoute } from './__root'

const schema = z
  .object({
    current_password: z.string().min(1),
    password: z.string().min(12, { message: 'min12' }).max(200),
    confirm: z.string().min(1),
  })
  .refine((v) => v.password === v.confirm, { path: ['confirm'], message: 'mismatch' })

type FormValues = z.infer<typeof schema>

// Top-level route (sibling to accept-invite / reset-password) so it
// sits outside both the unauthenticated auth-layout (which redirects
// signed-in users away) and the app-layout (whose beforeLoad sends any
// signed-in user with must_change_password=true here). Living outside
// both trees is what lets it be the actual destination of that redirect
// without bouncing back.
export const Route = createRoute({
  getParentRoute: () => rootRoute,
  path: '/change-password',
  beforeLoad: () => {
    const { isAuthenticated, user } = useAuthStore.getState()
    if (!isAuthenticated) {
      throw redirect({ to: '/auth/login' })
    }
    if (!user?.must_change_password) {
      throw redirect({ to: '/dashboard' })
    }
  },
  component: ChangePasswordPage,
})

function ChangePasswordPage() {
  const { t } = useTranslation(['auth', 'common'])
  const navigate = useNavigate()
  const setUser = useAuthStore((s) => s.setUser)
  const currentUser = useAuthStore((s) => s.user)
  const [showPassword, setShowPassword] = useState(false)

  const form = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: { current_password: '', password: '', confirm: '' },
  })

  const mutation = useMutation({
    mutationFn: (body: FormValues) =>
      api.my.changePassword({
        current_password: body.current_password,
        new_password: body.password,
      }),
    onSuccess: () => {
      if (currentUser) {
        setUser({ ...currentUser, must_change_password: false })
      }
      void navigate({ to: '/dashboard' })
    },
  })

  const onSubmit = form.handleSubmit(async (values) => {
    try {
      await mutation.mutateAsync(values)
    } catch (err) {
      if (err instanceof HttpError) {
        if (err.status === 401) {
          form.setError('current_password', { message: t('auth:current_password_wrong') })
        } else {
          form.setError('root', { message: err.message || t('common:error_generic') })
        }
      } else {
        form.setError('root', { message: t('common:error_network') })
      }
    }
  })

  return (
    <main className="flex min-h-dvh items-center justify-center bg-muted px-4 py-12">
      <motion.div
        initial={{ opacity: 0, y: 8 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.25 }}
        className={cn(
          'w-full max-w-md rounded-2xl border border-border bg-background p-8 shadow-sm',
          'dark:border-border',
        )}
      >
        <div className="flex flex-col gap-2">
          <div className="mb-2 flex h-10 w-10 items-center justify-center rounded-xl bg-muted text-foreground">
            <ShieldCheck className="h-5 w-5" />
          </div>
          <h1 className="text-2xl font-semibold tracking-tight text-foreground">
            {t('auth:force_change_title')}
          </h1>
          <p className="text-sm text-muted-foreground">{t('auth:force_change_subtitle')}</p>
        </div>

        <form onSubmit={onSubmit} noValidate className="mt-6 flex flex-col gap-4">
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="current_password">{t('auth:current_password')}</Label>
            <Input
              id="current_password"
              type={showPassword ? 'text' : 'password'}
              autoComplete="current-password"
              {...form.register('current_password')}
              aria-invalid={Boolean(form.formState.errors.current_password)}
            />
            {form.formState.errors.current_password && (
              <p className="text-xs text-red-600 dark:text-red-400">
                {form.formState.errors.current_password.message}
              </p>
            )}
          </div>

          <div className="flex flex-col gap-1.5">
            <Label htmlFor="password">{t('auth:new_password')}</Label>
            <div className="relative">
              <Input
                id="password"
                type={showPassword ? 'text' : 'password'}
                autoComplete="new-password"
                className="pr-10"
                {...form.register('password')}
              />
              <button
                type="button"
                onClick={() => setShowPassword((v) => !v)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                aria-label={showPassword ? t('auth:hide_password') : t('auth:show_password')}
              >
                {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
              </button>
            </div>
            {form.formState.errors.password && (
              <p className="text-xs text-red-600 dark:text-red-400">
                {form.formState.errors.password.message === 'min12'
                  ? t('auth:password_min12')
                  : t('common:error_generic')}
              </p>
            )}
            <p className="text-xs text-muted-foreground">{t('auth:password_hint')}</p>
          </div>

          <div className="flex flex-col gap-1.5">
            <Label htmlFor="confirm">{t('auth:confirm_password')}</Label>
            <Input
              id="confirm"
              type={showPassword ? 'text' : 'password'}
              autoComplete="new-password"
              {...form.register('confirm')}
            />
            {form.formState.errors.confirm && (
              <p className="text-xs text-red-600 dark:text-red-400">
                {t('auth:password_mismatch')}
              </p>
            )}
          </div>

          {form.formState.errors.root && (
            <Alert variant="danger">{form.formState.errors.root.message}</Alert>
          )}

          <Button
            type="submit"
            className="mt-2 w-full"
            disabled={form.formState.isSubmitting || mutation.isPending}
          >
            {mutation.isPending ? t('common:loading') : t('auth:force_change_submit')}
          </Button>
        </form>
      </motion.div>
    </main>
  )
}
